import { apiClient } from './api'

/**
 * Lightweight engagement telemetry: batches client events and flushes them to
 * `POST /api/events/{eventId}/track`. Attribution to the signed-in attendee is
 * handled server-side (via the session cookie). Best-effort — failures are
 * swallowed and never affect the UX.
 */

export type TrackKind =
  | 'app_open'
  | 'agenda_view'
  | 'session_view'
  | 'content_view'
  | 'content_download'
  | 'form_submit'

interface QueuedEvent {
  kind: TrackKind
  entityType?: string
  entityId?: string
  metadata?: Record<string, unknown>
}

let currentEventId: string | null = null
let queue: QueuedEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

/** Set/clear the event the telemetry is scoped to (called by the app). */
export function setTelemetryEvent(eventId: string | null) {
  currentEventId = eventId
}

async function flush() {
  flushTimer = null
  if (!currentEventId || queue.length === 0) return
  const events = queue
  queue = []
  try {
    await apiClient.post(`/events/${currentEventId}/track`, { events })
  } catch {
    // best-effort; drop on failure
  }
}

function scheduleFlush() {
  if (flushTimer) return
  flushTimer = setTimeout(flush, 2000)
}

/** Queue an engagement event. */
export function track(
  kind: TrackKind,
  opts: Omit<QueuedEvent, 'kind'> = {},
) {
  if (!currentEventId) return
  queue.push({ kind, ...opts })
  // Flush promptly for the first item; otherwise batch.
  if (queue.length >= 10) void flush()
  else scheduleFlush()
}

// Flush any pending events when the tab is hidden/closed.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush()
  })
  window.addEventListener('pagehide', () => void flush())
}
